import { Component, computed, input } from '@angular/core';

export type LegendItemColor =
  | 'emerald'
  | 'blue'
  | 'amber'
  | 'purple'
  | 'gray'
  | 'red'
  | 'teal'
  | 'indigo'
  | 'slate';

@Component({
  selector: 'lib-legend-item',
  standalone: true,
  host: {
    '[class]': 'hostClass()',
  },
  template: `
    <span class="msa-legend-item__dot"></span>
    <span class="msa-legend-item__label">{{ label() }}</span>
  `,
  styles: [`
    /* ═══════════════════════════════════════════════
       MSA LegendItem Atom
       ═══════════════════════════════════════════════
       Small colored dot + text label for legends.
       All styles on :host / :host(.class).
       ═══════════════════════════════════════════════ */

    :host {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 11.5px;
      color: #4b5563;
      font-family: inherit;
      line-height: 1;
    }

    /* ──────────────────────────────────────
       Dot
       ────────────────────────────────────── */
    .msa-legend-item__dot {
      width: 10px;
      height: 10px;
      border-radius: 2px;
      flex-shrink: 0;
    }

    /* ──────────────────────────────────────
       Label
       ────────────────────────────────────── */
    .msa-legend-item__label {
      white-space: nowrap;
    }

    /* ──────────────────────────────────────
       Colors — mapped from input
       ────────────────────────────────────── */
    :host(.msa-legend-item--emerald) .msa-legend-item__dot { background-color: #10b981; }
    :host(.msa-legend-item--blue)    .msa-legend-item__dot { background-color: #3b82f6; }
    :host(.msa-legend-item--amber)   .msa-legend-item__dot { background-color: #fbbf24; }
    :host(.msa-legend-item--purple)  .msa-legend-item__dot { background-color: #a78bfa; }
    :host(.msa-legend-item--gray)    .msa-legend-item__dot { background-color: #6b7280; }
    :host(.msa-legend-item--red)     .msa-legend-item__dot { background-color: #ef4444; }
    :host(.msa-legend-item--teal)    .msa-legend-item__dot { background-color: #14b8a6; }
    :host(.msa-legend-item--indigo)  .msa-legend-item__dot { background-color: #6366f1; }
    :host(.msa-legend-item--slate)   .msa-legend-item__dot { background-color: #1f2430; }
  `],
})
export class LegendItemComponent {
  /** Dot color (semantic name). */
  readonly color = input.required<LegendItemColor>();

  /** Text label next to the dot. */
  readonly label = input.required<string>();

  /** Host class binding — set from color input. */
  readonly hostClass = computed(() => `msa-legend-item--${this.color()}`);
}
